"use client";

import { useSnapshot } from "valtio";
import { authStore } from "../store/auth-store";
import { useAuthContext } from "../orchestrator/auth-orchestrator";
import { FirebaseSocialAdapter } from "../adapters/firebase-social";
import { EmailOtpAdapter } from "../adapters/email-otp";
import { NativeBridgeAdapter } from "../adapters/native-bridge";
import type { AuthStatus, AuthUser, AuthProvider } from "../types";

export interface LoginParams {
  email?: string;
  code?: string;
  forceSelect?: boolean;
}

export function useAuth() {
  const snap = useSnapshot(authStore);
  const { login: ctxLogin, logout } = useAuthContext();

  const status = snap.status as AuthStatus;
  const isAuthenticated = status.state === "authenticated";
  const isLoading =
    status.state === "restoring" || status.state === "authenticating";
  const user: AuthUser | null =
    status.state === "authenticated" ? status.user : null;

  const login = async (provider: AuthProvider, params?: LoginParams) => {
    const adapter = resolveAdapter(provider);
    await ctxLogin(adapter, {
      forceAccountSelection: params?.forceSelect,
      email: params?.email,
      code: params?.code,
    });
  };

  return { status, isAuthenticated, isLoading, user, login, logout };
}

function resolveAdapter(provider: AuthProvider) {
  if (provider === "email") {
    return new EmailOtpAdapter();
  }
  if (isInApp()) {
    return new NativeBridgeAdapter(provider);
  }
  return new FirebaseSocialAdapter(provider as "google" | "apple" | "twitter");
}

function isInApp(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return !!(
    (window as any).webkit?.messageHandlers?.bridge || (window as any).bridge
  );
}
